import { useState, useEffect, useCallback } from 'react';
import { getTrendingMovies, getMovieDetails } from '../services/tmdbApi';
import { getUserWatchlist, getUserWatchedlist, addToWatchlist, removeFromWatchlist } from '../services/userMovieInteractions';
import { getMovies } from '../services/mongoApi';

export const useMovies = (initialCategory = 'all') => {
  const [category, setCategory] = useState(initialCategory);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch movies by category (e.g. trending, all, upcoming)
  const fetchMovies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data = [];
      if (category === 'trending') {
        data = await getTrendingMovies();
      }
      // Extend here for other categories (upcoming, popular, etc.)
      else {
        data = []; // fallback empty array for unknown categories
      }
      setMovies(data);
    } catch (err) {
      setError('Failed to load movies');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchMovies();
  }, [fetchMovies]);

  // Search movies by query string
  const searchMovies = useCallback(async (filters) => {
    setSearchLoading(true);
    try {
      const result = await getMovies(filters);
      setSearchResults(result || []);
      setHasMore(result.has_more); // Assuming the API returns a has_more flag
      return result; // Add this line to return the result
    } catch (err) {
      setSearchResults([]);
      setError('Failed to search movies');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Get detailed movie info by ID
  const fetchMovieDetails = useCallback(async (movieId) => {
    try {
      const details = await getMovieDetails(movieId);
      return details;
    } catch {
      return null;
    }
  }, []);

  // Fetch user's watchlist movies from your MongoDB backend
  const getWatchlist = useCallback(async (userId) => {
    try {
      if (!userId) return [];
      return await getUserWatchlist(userId);
    } catch {
      return [];
    }
  }, []);

  // Fetch user's watched movies from MongoDB backend
  const getWatchedlist = useCallback(async (userId) => {
    try {
      if (!userId) return [];
      return await getUserWatchedlist(userId);
    } catch {
      return [];
    }
  }, []);

  // Add movie to user's watchlist
  const addMovieToWatchlist = useCallback(async (userId, movieId, status = 'WANT_TO_WATCH') => {
    try {
      return await addToWatchlist(userId, movieId, status);
    } catch {
      throw new Error('Failed to add movie to watchlist');
    }
  }, []);

  // Remove movie from user's watchlist
  const removeMovieFromWatchlist = useCallback(async (userId, movieId) => {
    try {
      return await removeFromWatchlist(userId, movieId);
    } catch {
      throw new Error('Failed to remove movie from watchlist');
    }
  }, []);

  return {
    category,
    setCategory,
    movies,
    loading,
    error,
    searchMovies,
    searchResults,
    searchLoading,
    fetchMovieDetails,
    getWatchlist,
    getWatchedlist,
    addMovieToWatchlist,
    removeMovieFromWatchlist,
  };
};
